import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import clsx from 'clsx';
import { PITCH_MAX_WORDS, PITCH_MIN_WORDS } from 'fym-shared/src/music24/outreach/pitchRules';
import { TypedMemo } from '../../../common/types';
import { SavedTextarea } from '../components/SavedTextarea';
import { useCampaignActions } from '../hooks/useCampaignActions';
import type { CampaignDetail } from '../outreachTypes';
import { MessagePreview } from './MessagePreview';
import { ProofPicker } from './ProofPicker';
import { QuestionPanel } from './QuestionPanel';
import { StepBar } from './StepBar';
import { BUILDER_ANGLES, BuilderAngle, NEXT_LABELS } from './builderSteps';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

export const ANGLE_COPY: Record<string, { title: MessageDescriptor; sub: MessageDescriptor }> = {
  auto: { title: messages.angleAuto, sub: messages.angleAutoSub },
  story: { title: messages.angleStory, sub: messages.angleStorySub },
  momentum: { title: messages.angleMomentum, sub: messages.angleMomentumSub },
  localScene: { title: messages.angleLocal, sub: messages.angleLocalSub },
};

interface StepMessageProps {
  accountId: number;
  campaign: CampaignDetail;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Step 3 "Message" (design outreach-v2 d1, d2): the angle (Plug picks for each person by default),
 * the artist's own words (untrusted text for prompt P4), the last line, the rules, the proof that
 * Plug may show (pitch email v3) and the sample pitch. The angle and the words save on change; the sample writes again only on "Write again".
 */
const StepMessageComponent: React.FC<StepMessageProps> = ({ accountId, campaign, onBack, onNext }) => {
  const intl = useIntl();
  const { updateCampaign } = useCampaignActions(accountId);
  const save = (patch: Parameters<typeof updateCampaign>[0]) => updateCampaign(patch).catch(console.error);
  const current: BuilderAngle = (BUILDER_ANGLES.find((angle) => angle === campaign.angle) ?? null) as BuilderAngle;
  const currentCopy = ANGLE_COPY[current ?? 'auto'] ?? ANGLE_COPY.auto;

  const angles = useMemo(
    () =>
      BUILDER_ANGLES.map((angle) => {
        const copy = ANGLE_COPY[angle ?? 'auto'] ?? ANGLE_COPY.auto;
        const on = angle === current;
        return (
          <button
            key={angle ?? 'auto'}
            type="button"
            className={clsx(styles.pickable, styles.option, on && styles.picked)}
            aria-pressed={on}
            onClick={() => updateCampaign({ campaignUuid: campaign.campaignUuid, angle }).catch(console.error)}
          >
            <span className={styles.radio} />
            <span>
              <b>{intl.formatMessage(copy?.title ?? messages.angleAuto)}</b>
              <small>{intl.formatMessage(copy?.sub ?? messages.angleAutoSub)}</small>
            </span>
          </button>
        );
      }),
    [campaign.campaignUuid, current, intl, updateCampaign]
  );

  return (
    <>
      <QuestionPanel title={intl.formatMessage(messages.angleTitle)} hint={intl.formatMessage(messages.angleHint)}>
        <div className={styles.options} data-testid="builder-angles">
          {angles}
        </div>
      </QuestionPanel>
      <QuestionPanel title={intl.formatMessage(messages.wordsTitle)} hint={intl.formatMessage(messages.wordsHintV2)}>
        <SavedTextarea
          label={intl.formatMessage(messages.wordsLabel)}
          value={campaign.userWords ?? ''}
          onSave={(userWords) =>
            save({ campaignUuid: campaign.campaignUuid, userWords: userWords.trim() === '' ? null : userWords })
          }
          maxLength={600}
          hint={intl.formatMessage(messages.wordsLimit)}
        />
        <SavedTextarea
          singleLine
          label={intl.formatMessage(messages.lockedLabel)}
          value={campaign.lockedLines ?? ''}
          onSave={(lockedLines) =>
            save({ campaignUuid: campaign.campaignUuid, lockedLines: lockedLines.trim() === '' ? null : lockedLines })
          }
          maxLength={500}
        />
        <div className={shared.alert}>
          <FontAwesomeIcon icon={faLock} className={shared.alertIcon} />
          <span>{intl.formatMessage(messages.rules, { min: PITCH_MIN_WORDS, max: PITCH_MAX_WORDS })}</span>
        </div>
      </QuestionPanel>
      <ProofPicker accountId={accountId} campaignUuid={campaign.campaignUuid} />
      <MessagePreview accountId={accountId} campaignUuid={campaign.campaignUuid} kinds={campaign.kinds} />
      <StepBar
        onBack={onBack}
        summary={
          current === null
            ? intl.formatMessage(messages.messageBarAuto)
            : intl.formatMessage(messages.messageBarPicked, {
                angle: intl.formatMessage(currentCopy?.title ?? messages.angleAuto),
              })
        }
        nextLabel={intl.formatMessage(NEXT_LABELS.sendFrom)}
        onNext={onNext}
      />
    </>
  );
};

export const StepMessage = TypedMemo(StepMessageComponent);
